/**
 * Zod schemas for the browse tag-showcases continuation endpoint
 * /api/search/tag-showcases
 *
 * Non-personalized companion to /api/recommendations/tag-showcases: seeds tag
 * rows from a category group (or all groups on the home view) so the showcase
 * stack keeps filling after the official category rows are exhausted. Reuses the
 * recommendations showcase item shape so rows render identically.
 */
import { z } from 'zod';
import { TagShowcaseSchema } from '../recommendations/tag-showcases';

// =============================================================================
// REQUEST
// =============================================================================

/** Parse a positive int query param, falling back to `fallback` on missing/garbage. */
const positiveIntParam = (fallback: number) =>
  z
    .string()
    .optional()
    .transform(v => {
      const n = parseInt(String(v ?? ''), 10);
      return Number.isFinite(n) && n > 0 ? n : fallback;
    });

export const SearchTagShowcasesQuerySchema = z.object({
  // Omitted on the home view (seed from all groups by popularity); set to scope
  // the continuation to one category group's tags. Garbage -> undefined (home).
  categoryGroupId: z.string().optional().transform(v => {
    const n = parseInt(String(v ?? ''), 10);
    return Number.isFinite(n) && n > 0 ? n : undefined;
  }),
  limit: positiveIntParam(4),
  listingsPerTag: positiveIntParam(12),
  minListingsPerTag: positiveIntParam(4),
  // Tags already rendered on previous pages. Sent as repeated params so tag
  // values containing commas survive; Next.js delivers repeats as string[].
  seenTags: z
    .union([z.string(), z.array(z.string())])
    .optional()
    .default([])
    .transform(v => (Array.isArray(v) ? v : v ? v.split(',') : []).map(s => s.trim()).filter(Boolean)),
  // Already-shown listing ids (CSV) to exclude from the new rows.
  seenListingIds: z
    .string()
    .optional()
    .default('')
    .transform(v => (v ? v.split(',').map(Number).filter(Number.isFinite) : [])),
});

export type SearchTagShowcasesQuery = z.infer<typeof SearchTagShowcasesQuerySchema>;

// =============================================================================
// RESPONSE
// =============================================================================

export const SearchTagShowcasesSuccessResponseSchema = z.object({
  success: z.literal(true),
  showcases: z.array(TagShowcaseSchema),
  hasMore: z.boolean(),
  totalAvailable: z.number(),
});

export type SearchTagShowcasesSuccessResponse = z.infer<typeof SearchTagShowcasesSuccessResponseSchema>;

export const SearchTagShowcasesErrorResponseSchema = z.object({
  success: z.literal(false),
  showcases: z.array(z.any()).default([]),
  error: z.string(),
});

export type SearchTagShowcasesErrorResponse = z.infer<typeof SearchTagShowcasesErrorResponseSchema>;

export const SearchTagShowcasesResponseSchema = z.discriminatedUnion('success', [
  SearchTagShowcasesSuccessResponseSchema,
  SearchTagShowcasesErrorResponseSchema,
]);

export type SearchTagShowcasesResponse = z.infer<typeof SearchTagShowcasesResponseSchema>;
